/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { RefObject, startTransition, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { __, getPermalink } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { Configure, InstantSearch, useHits, useSearchBox } from "react-instantsearch";
import CustomSuggestions from "@/components/atoms/search/customSuggestions";
import SearchBoxWithHint from "@/components/atoms/search/searchBox";
import { SearchSubmitButton } from "@/components/atoms/search/search-submit-button";
import { initMeiliSearch } from "@/lib/meilisearch";
import { InstantMeiliSearchInstance } from "@meilisearch/instant-meilisearch";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useRouter } from "next/navigation";

interface SearchBarProps {
  variant?: "default" | "navbar";
  locale: Locales;
}

export interface ProductHit {
  id: string;
  title: string;
  slug: string;
  prismicId: string;
  description: string;
  images: string[];
  categories: { lvl0: string; lvl1: string; lvl2: string };
  featuredImage: string;
  image_full_size_card: boolean;
  isCollection: boolean;
  collections: any[];
  tags: any[];
  display: number;
  length: string;
  width: string;
  price: number;
  sku: string;
  hasMedusaData: boolean;
  height: number;
  weight: string;
  diameter: number;
  conditioning: number;
  conditioning_bac?: number;
  is_surface_based?: boolean;
  is_price_dynamic?: boolean;
  product_type?: "Sale Product" | "Rent Product";
}

const SafeConfigure = Configure as React.ComponentType<any>;

const SearchResults = ({
  locale,
  setQuery,
  open,
  setOpen,
  isInNavbar,
}: {
  locale: Locales;
  setQuery: (query: string) => void;
  isInNavbar?: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
}) => {
  const { hits } = useHits<ProductHit>();
  const { query: currentQuery } = useSearchBox();

  useEffect(() => {
    if (!currentQuery && currentQuery.trim().length === 0 && hits.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [currentQuery, hits, setOpen]);

  const shouldShowSuggestions = currentQuery && currentQuery.trim().length > 0 && hits.length > 0;

  return (
    <>
      {shouldShowSuggestions && open && (
        <CustomSuggestions isInNavbar={isInNavbar} locale={locale} query={currentQuery} setQuery={setQuery} />
      )}
    </>
  );
};

const SearchBar: React.FC<SearchBarProps> = ({ variant = "default", locale }) => {
  const [searchClient, setSearchClient] = useState<InstantMeiliSearchInstance | null>(null);
  const [isClient, setIsClient] = useState(false);
  const [externalQuery, setExternalQuery] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const router = useRouter();

  useOnClickOutside(dropdownRef as RefObject<HTMLElement>, () => {
    setExternalQuery("");
    setOpen(false);
  });

  const isInNavbar = variant === "navbar";

  const indexName = `products_${locale}`;

  const getSearchHref = (query: string) => {
    const trimmedQuery = query.trim();
    const basePath = getPermalink(locale, "search");

    return trimmedQuery ? `${basePath}/${encodeURIComponent(trimmedQuery)}` : basePath;
  };

  const closeSearchBar = () => {
    setExternalQuery("");
    setOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const href = getSearchHref(externalQuery);

    closeSearchBar();

    startTransition(() => {
      router.push(href);
    });
  };

  useEffect(() => {
    try {
      const { searchClient: client } = initMeiliSearch();
      setSearchClient(client);
      setIsClient(true);
    } catch (error) {
      console.error("Erreur lors de l'initialisation de MeiliSearch:", error);
    }
  }, []);

  if (!isClient) return <SearchBarSkeleton variant={variant} />;

  return (
    <div ref={dropdownRef} className="relative mx-auto w-full xl:max-w-[968px]">
      <InstantSearch indexName={indexName} searchClient={searchClient as any}>
        <SafeConfigure hitsPerPage={12} sort={[`${indexName}:display:asc`, `${indexName}:lastEditDateFr:desc`]} />

        <label htmlFor="search-input" className="sr-only">
          {__("Search for products", locale)}
        </label>

        <form
          onSubmit={handleSubmit}
          className={cn(
            "focus-within:ring-blue-brand flex items-center rounded-md focus-within:ring-2",
            isInNavbar
              ? "bg-[#F9FAFB] max-xl:rounded-none max-xl:focus-within:ring-0"
              : "bg-white shadow-[0_2px_4px_0_#00000026]"
          )}
        >
          {!isInNavbar && (
            <div className="text-muted-foreground px-3">
              <Search size={24} aria-hidden="true" />
            </div>
          )}

          <SearchBoxWithHint
            locale={locale}
            isInNavbar={isInNavbar}
            query={externalQuery}
            onChangeQuery={setExternalQuery}
          />

          <SearchSubmitButton isInNavbar={isInNavbar} label={__("Search for products", locale)} />
        </form>

        {/* Suggestions box */}
        <div
          className={cn(
            "absolute z-[39] mt-2",
            isInNavbar ? "left-0 w-full xl:w-[800px] min-[81rem]:w-[900px]" : "w-full"
          )}
        >
          <SearchResults
            open={open}
            setOpen={setOpen}
            isInNavbar={isInNavbar}
            locale={locale}
            setQuery={setExternalQuery}
          />
        </div>
      </InstantSearch>
    </div>
  );
};

const SearchBarSkeleton: React.FC<{ variant?: "default" | "navbar" }> = ({ variant = "default" }) => {
  const isInNavbar = variant === "navbar";

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[968px] animate-pulse items-center rounded-md py-3.5",
        isInNavbar ? "bg-gray-200" : "bg-gray-100 shadow-[0_2px_4px_0_#00000026]"
      )}
    >
      {!isInNavbar && (
        <div className="text-muted-foreground px-3">
          <Search size={24} aria-hidden="true" />
        </div>
      )}
      <div className={cn(isInNavbar ? "h-5" : "h-7")}></div>
    </div>
  );
};

export default SearchBar;
